import axios from 'axios';
import { getToken, clearToken } from './tokenStore';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Convert a backend-relative avatar path (e.g. "/uploads/avatars/1_123.png")
 * into a full URL that works from the frontend origin.
 */
export function avatarSrc(path) {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${API_BASE_URL}${path}`;
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests + stamp a start time for perf logging
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.metadata = { start: performance.now() };
  return config;
});

// Log each API call's round-trip time alongside the backend's own timing +
// SQL-query count (from PerfMiddleware). A high query count on a slow call is
// the N+1 signature.
function logApiTiming(config, response, failed) {
  if (!config || !config.metadata) return;
  const ms = Math.round(performance.now() - config.metadata.start);
  const method = (config.method || 'get').toUpperCase();
  const url = (config.url || '').replace(config.baseURL || '', '');
  const serverMs = response && response.headers['x-process-time-ms'];
  const queries = response && response.headers['x-db-query-count'];
  const parts = [`${ms}ms round-trip`];
  if (serverMs) parts.push(`${Math.round(Number(serverMs))}ms server`);
  if (queries != null) parts.push(`${queries} queries`);
  const color = ms > 800 || Number(queries) >= 20 ? '#dc2626' : '#0891b2';
  // eslint-disable-next-line no-console
  console.log(
    `%c[api] ${method} ${url}${failed ? ' (failed)' : ''} — ${parts.join(', ')}`,
    `color:${color}`,
  );
}

// Handle expired/invalid tokens — emit event so AuthContext can logout
// without a full page reload
api.interceptors.response.use(
  (response) => {
    logApiTiming(response.config, response, false);
    return response;
  },
  (error) => {
    logApiTiming(error.config, error.response, true);
    // Only a 401 on a request that carried our token means the session is dead.
    // A failed sign-in (no token sent) is just a wrong password.
    if (
      error.response &&
      error.response.status === 401 &&
      error.config?.headers?.Authorization
    ) {
      clearToken();
      window.dispatchEvent(new Event('auth:unauthorized'));
    }
    if (error.response && error.response.status === 429) {
      const detail =
        (error.response.data && error.response.data.detail) ||
        "You're doing that too fast — please wait a moment and try again.";
      error.userMessage = detail;
      window.dispatchEvent(new CustomEvent('api:rate-limited', { detail }));
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (username, email, password) =>
    api.post('/auth/register', { username, email, password }),
  
  login: (username, password) =>
    api.post('/auth/token', 
      new URLSearchParams({ username, password }),
      { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
    ),
  
  getCurrentUser: () => api.get('/auth/me'),

  refresh: () => api.post('/auth/refresh'),

  forgotPassword: (identifier) => api.post('/auth/forgot-password', { identifier }),

  resetPassword: (token, newPassword) =>
    api.post('/auth/reset-password', { token, new_password: newPassword }),

  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),
};

// Books API
export const booksAPI = {
  list: () => api.get('/books'),
  get: (bookId) => api.get(`/books/${bookId}`),
  create: (book) => api.post('/books', book),
  search: (q) => api.get('/books/search', { params: { q } }),
  chapterCount: ({ title, author, isbn, pageCount }) =>
    api.get('/books/chapter-count', {
      params: { title, author, isbn, page_count: pageCount },
    }),
  editions: (bookId) => api.get(`/books/${bookId}/editions`),
  addEdition: (bookId, data) => api.post(`/books/${bookId}/editions`, data),
  proposeEdit: (bookId, data) => api.post(`/books/${bookId}/edit-proposals`, data),
  report: (bookId, reason) => api.post(`/books/${bookId}/report`, { reason }),
};

// Progress API
export const progressAPI = {
  get: (bookId) => api.get(`/progress/${bookId}`),
  getAll: () => api.get('/progress'),
  update: (bookId, data) => api.put(`/progress/${bookId}`, data),
  remove: (bookId) => api.delete(`/progress/${bookId}`),
};

// Discussions API
export const discussionsAPI = {
  list: (bookId, chapter = null) => {
    const params = chapter !== null ? { chapter } : {};
    return api.get(`/discussions/${bookId}`, { params });
  },
  
  create: (bookId, discussion) =>
    api.post(`/discussions/${bookId}`, discussion),
  
  getComments: (bookId, discussionId) =>
    api.get(`/discussions/${bookId}/${discussionId}/comments`),
  
  addComment: (bookId, discussionId, { content, parentId = null }) =>
    api.post(`/discussions/${bookId}/${discussionId}/comments`, {
      content,
      parent_id: parentId,
    }),

  likeDiscussion: (bookId, discussionId) =>
    api.post(`/discussions/${bookId}/${discussionId}/like`),

  likeComment: (bookId, discussionId, commentId) =>
    api.post(`/discussions/${bookId}/${discussionId}/comments/${commentId}/like`),

  reportDiscussion: (bookId, discussionId) =>
    api.post(`/discussions/${bookId}/${discussionId}/report`),

  reportComment: (bookId, discussionId, commentId) =>
    api.post(`/discussions/${bookId}/${discussionId}/comments/${commentId}/report`),

  deleteDiscussion: (bookId, discussionId) =>
    api.delete(`/discussions/${bookId}/${discussionId}`),

  deleteComment: (bookId, discussionId, commentId) =>
    api.delete(`/discussions/${bookId}/${discussionId}/comments/${commentId}`),
};

// Moderation API (admin)
export const moderationAPI = {
  restoreDiscussion: (discussionId) =>
    api.post(`/admin/moderation/discussions/${discussionId}/restore`),
  restoreComment: (commentId) =>
    api.post(`/admin/moderation/comments/${commentId}/restore`),
};

// Admin portal API (all require an admin token)
export const adminAPI = {
  // stats
  stats: () => api.get('/admin/stats'),
  abuseEvents: (limit = 100) => api.get('/admin/abuse-events', { params: { limit } }),
  config: () => api.get('/admin/config'),
  // users
  listUsers: (q = '', includeDeleted = false) =>
    api.get('/admin/users', { params: { q, include_deleted: includeDeleted } }),
  getUser: (id) => api.get(`/admin/users/${id}`),
  setUserAdmin: (id, isAdmin) => api.post(`/admin/users/${id}/admin`, { is_admin: isAdmin }),
  deleteUser: (id) => api.post(`/admin/users/${id}/delete`),
  restoreUser: (id) => api.post(`/admin/users/${id}/restore`),
  // books
  listBooks: (q = '', includeHidden = false) =>
    api.get('/admin/books', { params: { q, include_hidden: includeHidden } }),
  bookQueue: () => api.get('/admin/books/queue'),
  editProposals: () => api.get('/admin/edit-proposals'),
  bookReports: () => api.get('/admin/book-reports'),
  dismissBookReport: (id) => api.post(`/admin/book-reports/${id}/dismiss`),
  acceptProposal: (id) => api.post(`/admin/edit-proposals/${id}/accept`),
  rejectProposal: (id) => api.post(`/admin/edit-proposals/${id}/reject`),
  verifyBook: (id) => api.post(`/admin/books/${id}/verify`),
  updateBook: (id, patch) => api.patch(`/admin/books/${id}`, patch),
  hideBook: (id) => api.post(`/admin/books/${id}/hide`),
  unhideBook: (id) => api.post(`/admin/books/${id}/unhide`),
  // moderation
  queue: () => api.get('/admin/moderation/queue'),
  hideDiscussion: (id) => api.post(`/admin/moderation/discussions/${id}/hide`),
  restoreDiscussion: (id) => api.post(`/admin/moderation/discussions/${id}/restore`),
  hideComment: (id) => api.post(`/admin/moderation/comments/${id}/hide`),
  restoreComment: (id) => api.post(`/admin/moderation/comments/${id}/restore`),
  // reviews
  listReviews: (status = '') =>
    api.get('/admin/reviews', { params: status ? { status } : {} }),
  hideReview: (id) => api.post(`/admin/reviews/${id}/hide`),
  restoreReview: (id) => api.post(`/admin/reviews/${id}/restore`),
};

// Friends API
export const friendsAPI = {
  list: () => api.get('/friends'),

  getRequests: () => api.get('/friends/requests'),

  getSentRequests: () => api.get('/friends/sent'),

  suggestions: () => api.get('/friends/suggestions'),
  sendRequest: (userId) => api.post(`/friends/request/${userId}`),

  acceptRequest: (userId) => api.post(`/friends/accept/${userId}`),

  declineRequest: (userId) => api.post(`/friends/decline/${userId}`),

  removeFriend: (userId) => api.delete(`/friends/${userId}`),

  getFeed: (limit = 20, cursor = null) => {
    const params = { limit };
    if (cursor) params.cursor = cursor;
    return api.get('/friends/feed', { params });
  },
};

// Notifications API
export const notificationsAPI = {
  list: (limit = 30) => api.get('/notifications', { params: { limit } }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markAllRead: () => api.post('/notifications/read-all'),
  registerDevice: (token) => api.post('/notifications/devices', { token }),
  unregisterDevice: (token) => api.delete('/notifications/devices', { data: { token } }),
};

// Users API
export const usersAPI = {
  getProfile: (userId) => api.get(`/users/${userId}`),

  search: (query) => api.get('/users/search', { params: { q: query } }),

  // No body: App Store guideline 5.1.1(v) forbids a password step to delete an account.
  deleteAccount: () => api.delete('/users/me'),

  block: (userId) => api.post(`/users/${userId}/block`),
  unblock: (userId) => api.delete(`/users/${userId}/block`),
  listBlocks: () => api.get('/users/me/blocks'),
};

// Reviews API
export const reviewsAPI = {
  create: (bookId, data) => api.post(`/reviews/${bookId}`, data),
  list: (bookId) => api.get(`/reviews/${bookId}`),
  summary: (bookId) => api.get(`/reviews/${bookId}/summary`),
  delete: (bookId) => api.delete(`/reviews/${bookId}`),
  likeReview: (bookId, reviewId) =>
    api.post(`/reviews/${bookId}/${reviewId}/like`),
  getComments: (bookId, reviewId) =>
    api.get(`/reviews/${bookId}/${reviewId}/comments`),
  addComment: (bookId, reviewId, { content, parentId = null }) =>
    api.post(`/reviews/${bookId}/${reviewId}/comments`, {
      content,
      parent_id: parentId,
    }),
  likeComment: (bookId, reviewId, commentId) =>
    api.post(`/reviews/${bookId}/${reviewId}/comments/${commentId}/like`),

  deleteById: (bookId, reviewId) =>
    api.delete(`/reviews/${bookId}/${reviewId}`),
};

// Profile API
export const profileAPI = {
  updateBio: (bio) => api.put('/users/me', { bio }),
  updateName: (displayName) => api.put('/users/me', { display_name: displayName }),
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/users/me/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteAvatar: () => api.delete('/users/me/avatar'),
};

export default api;
